import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CommentType } from 'store/types/commentsTypes';
import * as api from '../../api/comments';
import { RootState } from 'store';

type TVote = {
  commentId: string;
  optionId: string;
};
// replies
export const getReplies = createAsyncThunk(
  'comments/getReplies',
  async ({ commentId, page, limit }: { commentId: string; page: number; limit: number }) =>
    api.getReplies(commentId, page, limit),
);
export const addReply = createAsyncThunk(
  'comments/addReply',
  async ({ comment, commentId, postId }: { comment: any; postId: string; commentId: string }) =>
    api.addReply(postId, commentId, comment),
);
export const deleteReply = createAsyncThunk(
  'comments/deleteReply',
  async ({ replyId, postId }: { replyId: string; postId: string; commentId: string }) =>
    api.deleteReply(replyId, postId),
);
// comments
export const getComments = createAsyncThunk(
  'comments/getComments',
  async ({ postId, page, limit }: { postId: string; page: number; limit: number }) =>
    api.getComments(postId, page, limit),
);

export const addComment = createAsyncThunk('comments/addComment', async ({ comment }: { comment: any }) =>
  api.addComment(comment),
);

export const deleteComment = createAsyncThunk(
  'comments/deleteComment',
  async ({ commentId, postId }: { commentId: string; postId?: string }) => api.deleteComment(commentId, postId),
);
// event comments
export const getEventComments = createAsyncThunk(
  'comments/getEventComments',
  async ({ eventId, page, limit }: { eventId: string; page: number; limit: number }) =>
    api.getEventComments(eventId, page, limit),
);

export const addEventComment = createAsyncThunk(
  'comments/addEventComment',
  async ({ comment, eventId }: { comment: any; eventId: string }) => api.addEventComment(eventId, comment),
);

export const deleteEventComment = createAsyncThunk(
  'comments/deleteEventComment',
  async ({ eventId, commentId }: { eventId: string; commentId: string }) => api.deleteEventComment(eventId, commentId),
);

export const surveyCommentsAddVote = createAsyncThunk(
  'comments/surveyCommentsAddVote',
  async ({ commentId, optionId }: TVote) => api.surveyCommentsAddVote(commentId, optionId),
);

export const surveyCommentsRemoveVote = createAsyncThunk(
  'comments/surveyCommentsRemoveVote',
  async ({ commentId, optionId }: TVote) => api.surveyCommentsRemoveVote(commentId, optionId),
);

export const surveyCommentsGetVotes = createAsyncThunk(
  'news/surveyCommentsGetVotes',
  async ({ commentId, optionId, page, limit }: TVote & { page: number; limit: number }) =>
    api.surveyCommentsGetVotes(commentId, optionId, page, limit),
);

interface State {
  comments: CommentType[];
  commentsIsLoading: boolean;
  commentsCount: number;
  commentsAndRepliesCount: number;
  eventComments: CommentType[];
  eventCommentsIsLoading: boolean;
  eventCommentsCount: number;
  //replies
  replies: { [key: string]: CommentType[] };
  repliesIsLoading: boolean;
  repliesCount: { [key: string]: number };
}

const initialState: State = {
  comments: [],
  commentsIsLoading: false,
  commentsCount: 0,
  eventComments: [],
  eventCommentsIsLoading: false,
  eventCommentsCount: 0,
  commentsAndRepliesCount: 0,
  replies: {},
  repliesIsLoading: false,
  repliesCount: {},
};

const commentsSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    clearComments: (state) => {
      state.comments = [];
      state.commentsCount = 0;
      state.commentsAndRepliesCount = 0;
    },
    clearEventComments: (state) => {
      state.eventComments = [];
      state.eventCommentsCount = 0;
    },
    modifyComments: (
      state,
      action: PayloadAction<{
        type: 'comments' | 'eventComments';
        comment: any;
      }>,
    ) => {
      const idx = state[action.payload.type].findIndex((el) => el?._id === action.payload.comment?._id);
      state[action.payload.type][idx] = action.payload.comment;
    },
    clearReply: (state) => {
      state.replies = {};
      state.repliesCount = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getComments.pending, (state) => {
      state.commentsIsLoading = true;
    });
    builder.addCase(getComments.fulfilled, (state, action: any) => {
      state.commentsIsLoading = false;
      state.commentsCount = action.payload.total;
      state.commentsAndRepliesCount = action.payload.commentsAndRepliesCount;
      state.comments = state.comments.concat(action.payload.data);
    });
    builder.addCase(getComments.rejected, (state) => {
      state.commentsIsLoading = false;
    });

    builder.addCase(addComment.pending, (state) => {
      state.commentsIsLoading = true;
    });
    builder.addCase(addComment.fulfilled, (state, action: any) => {
      state.commentsIsLoading = false;
      state.commentsCount = state.commentsCount + 1;
      state.commentsAndRepliesCount = state.commentsAndRepliesCount + 1;
      state.comments = [action.payload, ...state.comments];
    });
    builder.addCase(addComment.rejected, (state) => {
      state.commentsIsLoading = false;
    });

    builder.addCase(deleteComment.pending, (state) => {
      state.commentsIsLoading = true;
    });
    builder.addCase(deleteComment.fulfilled, (state, action: any) => {
      const commentId = action.meta.arg.commentId;
      const commentToDelete: any = state.comments.find((comment: any) => comment._id === commentId);
      const repliesCount = commentToDelete ? commentToDelete.replyCount || 0 : 0;
      const decrement = repliesCount + 1;
      state.comments = state.comments.filter((comment: any) => comment._id !== commentId);
      state.commentsIsLoading = false;
      state.commentsCount = Math.max(0, state.commentsCount - 1);
      state.commentsAndRepliesCount = Math.max(0, state.commentsAndRepliesCount - decrement);
    });
    builder.addCase(deleteComment.rejected, (state) => {
      state.commentsIsLoading = false;
    });
    // replies
    builder.addCase(getReplies.pending, (state) => {
      state.repliesIsLoading = true;
    });
    builder.addCase(getReplies.fulfilled, (state, action: any) => {
      const commentId = action.payload.data[0].commentedEntityId;
      state.repliesIsLoading = false;
      const prevReplies = state.replies[commentId] || [];
      const parentComment = state.comments.find((c) => c._id === commentId);
      const filteredNewReplies = (() => {
        if (!parentComment?.replyPreviews || !Array.isArray(parentComment.replyPreviews)) {
          return action.payload.data;
        }
        const previewIds = new Set(parentComment.replyPreviews.map((p: any) => p?._id || p?.id).filter(Boolean));
        return action.payload.data.filter((r: any) => !previewIds.has(r._id));
      })();
      state.replies = {
        ...state.replies,
        [commentId]: [...prevReplies, ...filteredNewReplies].filter(
          (reply, index, self) => self.findIndex((r) => r._id === reply._id) === index,
        ),
      };
      state.repliesCount = {
        ...state.repliesCount,
        [commentId]: action.payload.total,
      };
    });
    builder.addCase(getReplies.rejected, (state) => {
      state.repliesIsLoading = false;
    });
    builder.addCase(addReply.pending, (state) => {
      state.repliesIsLoading = true;
    });
    builder.addCase(addReply.fulfilled, (state, action: any) => {
      try {
        state.repliesIsLoading = false;
        const commentId = action.payload.commentedEntityId;
        const prevReplies = state.replies[commentId] || [];
        state.repliesCount = {
          ...state.repliesCount,
          [commentId]: (state.repliesCount[commentId] || 0) + 1,
        };
        state.commentsAndRepliesCount = state.commentsAndRepliesCount + 1;
        const parentComment = state.comments.find((e) => e._id === commentId);

        if (parentComment) {
          parentComment.replyCount = (parentComment.replyCount || 0) + 1;
        }
        if (!parentComment.replyPreviews) {
          parentComment.replyPreviews = [];
        }

        if (parentComment.replyPreviews?.length < 3) {
          parentComment.replyPreviews.push(action.payload);
        } else {
          state.replies = {
            ...state.replies,
            [commentId]: [action.payload, ...prevReplies],
          };
        }
      } catch (e) {
        console.log(e);
      }
    });
    builder.addCase(addReply.rejected, (state) => {
      state.repliesIsLoading = false;
    });

    builder.addCase(deleteReply.pending, (state) => {
      state.repliesIsLoading = true;
    });
    builder.addCase(deleteReply.fulfilled, (state, action: any) => {
      const replyId = action.meta.arg.replyId;
      const parentCommentId = action.meta.arg.commentId;
      state.repliesIsLoading = false;
      if (parentCommentId && state.replies[parentCommentId]) {
        const prev = state.replies[parentCommentId];
        const filtered = prev.filter((r) => r?._id !== replyId);
        if (filtered.length === 0) {
          delete state.replies[parentCommentId];
        } else {
          state.replies[parentCommentId] = filtered;
        }
        if (typeof state.repliesCount[parentCommentId] === 'number') {
          state.repliesCount[parentCommentId] = Math.max(0, state.repliesCount[parentCommentId] - 1);
        }
      } else {
        Object.keys(state.replies).forEach((key) => {
          const arr = state.replies[key];
          const newArr = arr.filter((r) => r?._id !== replyId);
          if (newArr.length !== arr.length) {
            if (newArr.length) state.replies[key] = newArr;
            else delete state.replies[key];
            if (typeof state.repliesCount[key] === 'number') {
              state.repliesCount[key] = Math.max(0, state.repliesCount[key] - 1);
            }
          }
        });
      }
      const parentComment =
        state.comments.find((c) => c._id === parentCommentId) ||
        state.eventComments.find((c) => c._id === parentCommentId);
      if (parentComment && Array.isArray(parentComment.replyPreviews)) {
        const idx = parentComment.replyPreviews.findIndex((r) => r?._id === replyId);
        if (idx !== -1) {
          parentComment.replyPreviews.splice(idx, 1);
          parentComment.replyCount = Math.max(0, (parentComment.replyCount || 0) - 1);
        } else {
          if (typeof parentComment.replyCount === 'number') {
            parentComment.replyCount = Math.max(0, parentComment.replyCount - 1);
          }
        }
      } else if (parentComment) {
        if (typeof parentComment.replyCount === 'number') {
          parentComment.replyCount = Math.max(0, parentComment.replyCount - 1);
        }
      }
      state.commentsAndRepliesCount = Math.max(0, state.commentsAndRepliesCount - 1);
    });
    builder.addCase(deleteReply.rejected, (state, act) => {
      state.repliesIsLoading = false;
    });
    // event comments
    builder.addCase(getEventComments.pending, (state) => {
      state.eventCommentsIsLoading = true;
    });
    builder.addCase(getEventComments.fulfilled, (state, action: any) => {
      state.eventCommentsIsLoading = false;
      state.eventCommentsCount = action.payload.total;
      state.eventComments = state.eventComments.concat(action.payload.data);
    });
    builder.addCase(getEventComments.rejected, (state) => {
      state.eventCommentsIsLoading = false;
    });

    builder.addCase(addEventComment.pending, (state) => {
      state.eventCommentsIsLoading = true;
    });
    builder.addCase(addEventComment.fulfilled, (state, action: any) => {
      state.eventCommentsIsLoading = false;
      state.eventCommentsCount = state.eventCommentsCount + 1;
      state.eventComments = [action.payload, ...state.eventComments];
    });
    builder.addCase(addEventComment.rejected, (state) => {
      state.eventCommentsIsLoading = false;
    });

    builder.addCase(deleteEventComment.pending, (state) => {
      state.eventCommentsIsLoading = true;
    });
    builder.addCase(deleteEventComment.fulfilled, (state, action) => {
      const commentsArr: Array<any> = state.eventComments.filter(
        (comment: any) => comment._id !== action.meta.arg.commentId,
      );
      state.eventCommentsIsLoading = false;
      state.eventCommentsCount = state.eventCommentsCount - 1;
      state.eventComments = commentsArr;
    });
    builder.addCase(deleteEventComment.rejected, (state) => {
      state.eventCommentsIsLoading = false;
    });

    builder.addCase(surveyCommentsAddVote.pending, (state) => {
      state.commentsIsLoading = true;
    });
    builder.addCase(surveyCommentsAddVote.fulfilled, (state, action: any) => {
      state.commentsIsLoading = false;

      const idx = state.comments.findIndex((post) => post?._id === action.payload._id);
      // @ts-ignore
      if (idx !== -1) state.comments[idx].survey = action.payload.survey;

      // if (state.currentNews?._id === action.payload._id) {
      //   state.currentNews.survey = action.payload.survey;
      // }

      // const kreiseIdx = state.kreiseNews.findIndex((post) => post?._id === action.payload._id);
      // if (kreiseIdx !== -1) state.kreiseNews[kreiseIdx].survey = action.payload.survey;
    });
    builder.addCase(surveyCommentsAddVote.rejected, (state) => {
      state.commentsIsLoading = false;
    });

    builder.addCase(surveyCommentsRemoveVote.pending, (state) => {
      state.commentsIsLoading = true;
    });
    builder.addCase(surveyCommentsRemoveVote.fulfilled, (state, action: any) => {
      state.commentsIsLoading = false;
      const idx = state.comments.findIndex((post) => post?._id === action.payload._id);
      // @ts-ignore
      if (idx !== -1) state.comments[idx].survey = action.payload.survey;

      // if (state.currentNews?._id === action.payload._id) {
      //   state.currentNews.survey = action.payload.survey;
      // }

      // const kreiseIdx = state.kreiseNews.findIndex((post) => post?._id === action.payload._id);
      // if (kreiseIdx !== -1) state.kreiseNews[kreiseIdx].survey = action.payload.survey;
    });
    builder.addCase(surveyCommentsRemoveVote.rejected, (state) => {
      state.commentsIsLoading = false;
    });
  },
});

export const selectCommentState = (state: RootState) => state.comments;

export const commentsActions = commentsSlice.actions;

export const comments = commentsSlice.reducer;

export const { clearComments, clearEventComments, modifyComments } = commentsSlice.actions;
